'use client';

import clsx from 'clsx';
import { isString } from 'lodash-es';

import { type ElementType, type JSX, type ReactElement, cloneElement, useRef } from 'react';

import Image, { type StaticImageData } from 'next/image';

import type { CarbonIconProps } from '@carbon/icons-react';
import { Pause, Play } from '@carbon/icons-react';
import { IconButton, Tile } from '@carbon/react';

import {
  InteractiveGroup,
  type Props as InteractiveGroupProps,
} from '@/components/InteractiveGroup';
import { useTogglePlayPause } from '@/utils/hooks/useTogglePlayPause';

import styles from './product-card.module.scss';

const IMAGE_SIZE = [1248, 600];

type MediaItem =
  | {
      videoSrc?: string;
      image?: never;
    }
  | {
      videoSrc?: never;
      image?: {
        src: string | StaticImageData;
        alt: string;
      };
    };

type HeadingLevel = 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

export type Props = {
  as?: ElementType;
  title: string;
  description: string;
  icon?: ReactElement<CarbonIconProps>;
  primaryCta?: InteractiveGroupProps['primary'];
  secondaryCta?: InteractiveGroupProps['secondary'];
  tertiaryCta?: InteractiveGroupProps['tertiary'];
  variant?: 'light' | 'faint' | 'dark';
  headingLevel?: HeadingLevel;
  stackedLayout?: boolean;
  mobileVideoSrc?: string;
  className?: string;
} & MediaItem;

export function ProductCard({
  as = 'div',
  videoSrc,
  mobileVideoSrc,
  image,
  title,
  description,
  className,
  icon,
  primaryCta,
  secondaryCta,
  tertiaryCta,
  variant = 'light',
  headingLevel = 'h3',
  stackedLayout = false,
}: Props) {
  const BaseComponent: ElementType = as;
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const { togglePlayPause, isPlaying } = useTogglePlayPause(videoRef);
  const iconDefaultClassName = icon?.props.className || [];
  const renderIcon = icon
    ? cloneElement(icon, {
        className: clsx(styles['product-card__icon'], iconDefaultClassName),
        size: '32',
        height: '32',
        width: '32',
      })
    : null;

  const mediaClassName =
    image || videoSrc ? styles[`product-card--${image ? 'image' : 'video'}`] : null;

  const validLevels: HeadingLevel[] = ['h2', 'h3', 'h4', 'h5', 'h6'];
  const safeLevel = validLevels.includes(headingLevel) ? headingLevel : 'h3';
  const Heading = safeLevel as keyof JSX.IntrinsicElements;

  return (
    <BaseComponent>
      <Tile
        className={clsx(
          styles['product-card'],
          mediaClassName,
          styles[`product-card--${variant}`],
          stackedLayout && styles['product-card--stacked'],
          className
        )}
      >
        <div
          className={clsx(
            styles['product-card__content'],
            stackedLayout && styles['product-card__content--stacked']
          )}
        >
          <div>
            {renderIcon && renderIcon}
            <Heading className={styles['product-card__title']}>{title}</Heading>
            <p className={styles['product-card__description']}>{description}</p>
          </div>
          {primaryCta && (
            <InteractiveGroup
              primary={primaryCta}
              secondary={secondaryCta}
              tertiary={tertiaryCta}
              narrow
            />
          )}
        </div>
        {videoSrc && (
          <>
            <video
              ref={videoRef}
              src={videoSrc}
              muted
              autoPlay
              loop
              className={clsx(
                styles['product-card__bg'],
                styles['product-card__bg--desktop'],
                stackedLayout && styles['product-card__bg--stacked']
              )}
            ></video>

            {mobileVideoSrc && ( // if optional mobileVideoSrc
              <video
                src={mobileVideoSrc}
                muted
                autoPlay
                loop
                className={clsx(
                  styles['product-card__bg'],
                  styles['product-card__bg--mobile'],
                  stackedLayout && styles['product-card__bg--stacked']
                )}
              ></video>
            )}

            <div className={styles['product-card__media-button']}>
              <IconButton
                align="top"
                size="sm"
                kind="secondary"
                label={isPlaying ? 'Pause' : 'Play'}
                onClick={togglePlayPause}
              >
                {isPlaying ? <Pause /> : <Play />}
              </IconButton>
            </div>
          </>
        )}
        {image && (
          <Image
            width={!isString(image.src) && 'width' in image.src ? image.src.width : IMAGE_SIZE[0]}
            height={
              !isString(image.src) && 'height' in image.src ? image.src.height : IMAGE_SIZE[1]
            }
            src={image.src}
            alt={image.alt}
            className={clsx(
              styles['product-card__bg'],
              stackedLayout && styles['product-card__bg--stacked']
            )}
          />
        )}
      </Tile>
    </BaseComponent>
  );
}
